function credentialsApi() {
  if (
    typeof window === "undefined" ||
    !window.isSecureContext ||
    !window.navigator ||
    !window.navigator.credentials
  ) {
    return null;
  }
  return window.navigator.credentials;
}

const VAULT_DATABASE_NAME = "smartnecklace-login-vault";
const VAULT_DATABASE_VERSION = 1;
const VAULT_STORE_NAME = "credentials";
const VAULT_RECORD_ID = "remembered-login";

function vaultEnvironment() {
  if (
    typeof window === "undefined" ||
    !window.isSecureContext ||
    !window.indexedDB ||
    !window.crypto ||
    !window.crypto.subtle ||
    typeof window.TextEncoder !== "function" ||
    typeof window.TextDecoder !== "function"
  ) {
    return null;
  }
  return {
    indexedDB: window.indexedDB,
    crypto: window.crypto,
    TextEncoder: window.TextEncoder,
    TextDecoder: window.TextDecoder,
  };
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("浏览器凭据存储失败"));
  });
}

function transactionComplete(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error || new Error("浏览器凭据事务失败"));
    transaction.onabort = () =>
      reject(transaction.error || new Error("浏览器凭据事务已取消"));
  });
}

function openVaultDatabase(indexedDB) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(VAULT_DATABASE_NAME, VAULT_DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(VAULT_STORE_NAME)) {
        database.createObjectStore(VAULT_STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("无法打开浏览器凭据库"));
  });
}

export async function encryptCredentialPayload(environment, account, password) {
  const key = await environment.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
  const iv = environment.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new environment.TextEncoder().encode(
    JSON.stringify({ account, password })
  );
  const ciphertext = await environment.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    plaintext
  );
  return {
    id: VAULT_RECORD_ID,
    key,
    iv: Array.from(iv),
    ciphertext,
  };
}

export async function decryptCredentialPayload(environment, record) {
  if (!record || !record.key || !record.iv || !record.ciphertext) {
    return null;
  }
  const plaintext = await environment.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(record.iv) },
    record.key,
    record.ciphertext
  );
  const credential = JSON.parse(new environment.TextDecoder().decode(plaintext));
  if (
    !credential ||
    typeof credential.account !== "string" ||
    !credential.account ||
    typeof credential.password !== "string"
  ) {
    return null;
  }
  return credential;
}

async function loadEncryptedCredential(expectedAccount) {
  const environment = vaultEnvironment();
  if (!environment) return null;
  let database;
  try {
    database = await openVaultDatabase(environment.indexedDB);
    const transaction = database.transaction(VAULT_STORE_NAME, "readonly");
    const record = await requestResult(
      transaction.objectStore(VAULT_STORE_NAME).get(VAULT_RECORD_ID)
    );
    const credential = await decryptCredentialPayload(environment, record);
    if (expectedAccount && credential && credential.account !== expectedAccount) {
      return null;
    }
    return credential;
  } catch (error) {
    return null;
  } finally {
    if (database) database.close();
  }
}

async function storeEncryptedCredential(account, password) {
  const environment = vaultEnvironment();
  if (!environment) return false;
  let database;
  try {
    const record = await encryptCredentialPayload(environment, account, password);
    database = await openVaultDatabase(environment.indexedDB);
    const transaction = database.transaction(VAULT_STORE_NAME, "readwrite");
    const completion = transactionComplete(transaction);
    transaction.objectStore(VAULT_STORE_NAME).put(record);
    await completion;
    return true;
  } catch (error) {
    return false;
  } finally {
    if (database) database.close();
  }
}

async function clearEncryptedCredential() {
  const environment = vaultEnvironment();
  if (!environment) return;
  let database;
  try {
    database = await openVaultDatabase(environment.indexedDB);
    const transaction = database.transaction(VAULT_STORE_NAME, "readwrite");
    const completion = transactionComplete(transaction);
    transaction.objectStore(VAULT_STORE_NAME).delete(VAULT_RECORD_ID);
    await completion;
  } catch (error) {
    // Clearing an unavailable browser vault must not block the checkbox action.
  } finally {
    if (database) database.close();
  }
}

export async function loadBrowserCredential(remember, expectedAccount = "") {
  const api = credentialsApi();
  if (!remember) {
    return null;
  }
  if (api && typeof api.get === "function") {
    try {
      const credential = await api.get({
        password: true,
        mediation: "optional",
      });
      if (
        credential &&
        credential.id &&
        typeof credential.password === "string" &&
        (!expectedAccount || credential.id === expectedAccount)
      ) {
        return {
          account: credential.id,
          password: credential.password,
        };
      }
    } catch (error) {
      // Fall back to the encrypted same-browser vault below.
    }
  }
  return loadEncryptedCredential(expectedAccount);
}

export async function storeBrowserCredentialWithFallback(
  api,
  PasswordCredentialClass,
  encryptedStore,
  account,
  password,
  encryptedClear = async () => {}
) {
  let passwordManagerStored = false;
  if (
    api &&
    typeof api.store === "function" &&
    typeof PasswordCredentialClass === "function"
  ) {
    try {
      const credential = new PasswordCredentialClass({
        id: account,
        name: account,
        password,
      });
      await api.store(credential);
      passwordManagerStored = true;
    } catch (error) {
      // The encrypted same-browser vault remains available as a fallback.
    }
  }
  if (passwordManagerStored) {
    try {
      await encryptedClear();
    } catch (error) {
      // Removing a stale fallback must not turn a successful browser save into a failure.
    }
    return true;
  }
  return encryptedStore(account, password);
}

export async function storeBrowserCredential(account, password) {
  const api = credentialsApi();
  const PasswordCredentialClass =
    typeof window !== "undefined" ? window.PasswordCredential : undefined;
  return storeBrowserCredentialWithFallback(
    api,
    PasswordCredentialClass,
    storeEncryptedCredential,
    account,
    password,
    clearEncryptedCredential
  );
}

export async function preventSilentCredentialAccess() {
  await clearEncryptedCredential();
  const api = credentialsApi();
  if (!api || typeof api.preventSilentAccess !== "function") {
    return;
  }
  try {
    await api.preventSilentAccess();
  } catch (error) {
    // Browser credential controls are best-effort and must not block login.
  }
}
